import { useState } from 'react';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Loader2, Check, AlertCircle } from 'lucide-react';

export default function PasswordChange() {
    const { user } = useAuth();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handlePasswordChange = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || !user.email) return;

        setError('');
        setSuccess(false);

        if (newPassword !== confirmPassword) {
            setError("New passwords do not match.");
            return;
        }

        if (newPassword.length < 6) {
            setError("Password must be at least 6 characters.");
            return;
        }

        setLoading(true);

        try {
            // Re-authenticate first (security requirement)
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);

            // Update password
            await updatePassword(user, newPassword);

            setSuccess(true);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setTimeout(() => setSuccess(false), 5000);
        } catch (err: any) {
            console.error("Password change error", err);
            if (err.code === 'auth/wrong-password') {
                setError("Current password is incorrect.");
            } else if (err.code === 'auth/requires-recent-login') {
                setError("For security, please log out and log back in before changing your password.");
            } else {
                setError(err.message || "Failed to update password.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="card p-6 border-white/5 bg-secondary/30">
            <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                <Lock className="w-5 h-5 text-purple-400" /> Security
            </h3>

            <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
                {error && (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2 text-sm text-red-400">
                        <AlertCircle className="w-4 h-4 mt-0.5" />
                        {error}
                    </div>
                )}

                {success && (
                    <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-2 text-sm text-green-400">
                        <Check className="w-4 h-4" />
                        Password updated successfully.
                    </div>
                )}

                <div>
                    <label className="text-xs text-gray-400 uppercase font-bold block mb-1">Current Password</label>
                    <input
                        type="password"
                        required
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="input-field"
                    />
                </div>

                <div>
                    <label className="text-xs text-gray-400 uppercase font-bold block mb-1">New Password</label>
                    <input
                        type="password"
                        required
                        minLength={6}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="input-field"
                    />
                </div>

                <div>
                    <label className="text-xs text-gray-400 uppercase font-bold block mb-1">Confirm New Password</label>
                    <input
                        type="password"
                        required
                        minLength={6}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="input-field"
                    />
                </div>

                <div className="pt-2">
                    <button
                        type="submit"
                        disabled={loading}
                        className="btn btn-primary w-full md:w-auto"
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update Password'}
                    </button>
                </div>
            </form>
        </div>
    );
}
